import { Injectable, inject } from '@angular/core';
import { Page, ServiceHandler } from '@nucleus/ui-base';
import {
  AuditControllerService,
  NucleusAudit,
  NucleusAuditFilter,
  NucleusAuditServiceContract,
} from '@nucleus/audit-report-ui';

import { AuditService } from '../audit/audit.service';
import { FilterRequest } from '../filter-request';
import { RequestHandler } from '../request-handler';

/**
 * Bridges TaskSense's concrete AuditService to the lib's
 * NucleusAuditServiceContract. Most methods pass straight through —
 * TaskSense already uses the `findX` naming. Reports take a
 * FilterRequest envelope on the TaskSense side, so we wrap (filter,
 * page) before delegating.
 *
 * Wired in `@nucleus/audit-report-ui`'s NUCLEUS_AUDIT_SERVICE token
 * via `NUCLEUS_AUDIT_UI_PROVIDERS` in the index barrel.
 */
@Injectable({ providedIn: 'root' })
export class TasksenseAuditServiceAdapter implements NucleusAuditServiceContract {
  private readonly delegate = inject(AuditService);
  private readonly api = inject(AuditControllerService);
  private readonly requestHandler = inject(RequestHandler);

  search(handler: ServiceHandler<NucleusAudit[]>, _clientId?: string): void {
    this.delegate.search(handler as any);
  }

  filter(handler: any, filter: NucleusAuditFilter, page?: Page, _clientId?: string): void {
    this.delegate.filter(handler, filter, page);
  }

  findEntities(handler: ServiceHandler<string[]>): void {
    this.delegate.findEntities(handler);
  }

  findActionCodes(handler: ServiceHandler<string[]>): void {
    this.delegate.findActionCodes(handler);
  }

  /** NUC-261 — full audit row for the detail dialog's lazy snapshot fetch. */
  findById(handler: ServiceHandler<NucleusAudit>, id: number): void {
    this.requestHandler.handleRequest(
      this.api.findById(id),
      handler as any,
      false,
      'Loading audit record',
    );
  }

  getPDFReport(handler: ServiceHandler<Blob>, filter: NucleusAuditFilter, page?: Page): void {
    this.delegate.getPDFReport(handler, this.toRequest(filter, page));
  }

  getXLSReport(handler: ServiceHandler<Blob>, filter: NucleusAuditFilter, page?: Page): void {
    this.delegate.getXLSReport(handler, this.toRequest(filter, page));
  }

  getCSVReport(handler: ServiceHandler<Blob>, filter: NucleusAuditFilter, page?: Page): void {
    this.delegate.getCSVReport(handler, this.toRequest(filter, page));
  }

  private toRequest(filter: NucleusAuditFilter, page?: Page): FilterRequest<NucleusAuditFilter> {
    const req = new FilterRequest<NucleusAuditFilter>(filter);
    if (page) req.page = page;
    return req;
  }
}
